// Shared helpers for the Reports module.

// §14.5 display rule: "EUR 1.234,56" — currency code before amount, dot for
// thousands, comma for decimals, always exactly 2 decimals.
export const formatEUR = (value) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  const sign = safe < 0 ? '-' : '';
  const abs = Math.abs(safe);
  const [intPart, decPart] = abs.toFixed(2).split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `EUR ${sign}${grouped},${decPart}`;
};

// 'YYYY-MM-DD' → '15/07/2026' (parsed directly, no timezone drift).
export const formatReportDate = (ymd) => {
  if (!ymd) return '—';
  const m = String(ymd).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '—';
  const month = Number(m[2]);
  if (month < 1 || month > 12) return '—';
  return `${m[3]}/${m[2]}/${m[1]}`;
};

// Generic client-side sorter. Strings compare case-insensitively, numbers
// numerically, null/empty always sink to the bottom.
export const sortRows = (rows, key, dir) => {
  const mul = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    const aEmpty = av === null || av === undefined || av === '';
    const bEmpty = bv === null || bv === undefined || bv === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul;
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * mul;
  });
};

// CSV export — quotes every field, prefixes BOM so Excel opens UTF-8 correctly.
export const downloadCSV = (filename, headers, rows) => {
  const quote = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    headers.map(quote).join(';'),
    ...rows.map((r) => r.map(quote).join(';')),
  ];
  const blob = new Blob([`﻿${lines.join('\r\n')}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const escapeHtml = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// Excel export — no external dependency. An HTML <table> served with the
// application/vnd.ms-excel MIME type opens natively in Excel with real columns,
// so we build a spreadsheet-shaped HTML document and save it as .xls.
export const downloadExcel = (filename, headers, rows, title = '', filterLines = []) => {
  const span = Math.max(1, headers.length);
  const headHtml = headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
  const bodyHtml = rows
    .map((r) => `<tr>${r.map((c) => `<td>${escapeHtml(c)}</td>`).join('')}</tr>`)
    .join('');
  const titleRow = title
    ? `<tr><td colspan="${span}" style="font-weight:bold;font-size:14px">${escapeHtml(title)}</td></tr>`
    : '';
  // Reflect the applied filters (and search) at the top of the sheet.
  const filterRows = (filterLines && filterLines.length)
    ? `<tr><td colspan="${span}" style="font-weight:bold;color:#475467">Applied Filters</td></tr>` +
      filterLines
        .map(([k, v]) => `<tr><td style="font-weight:bold;background:#f8f9fb">${escapeHtml(k)}</td><td colspan="${span - 1}">${escapeHtml(v)}</td></tr>`)
        .join('')
    : '';
  const gap = (titleRow || filterRows) ? '<tr></tr>' : '';
  const html =
    `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head>` +
    `<meta charset="UTF-8"/>` +
    `<style>table{border-collapse:collapse}th,td{border:1px solid #d0d5dd;padding:4px 8px;font-family:Arial,sans-serif;font-size:12px;mso-number-format:"\\@"}th{background:#f2f4f7;font-weight:bold;text-align:left}</style>` +
    `</head><body><table>${titleRow}${filterRows}${gap}<thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;

  const blob = new Blob(['﻿', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Opens a print-ready window for the given HTML body and triggers the browser
// print dialog. Used for both "Print" and "Export PDF" (Save as PDF in the
// dialog). Returns false if the window was blocked by a popup blocker.
export const printHtml = (bodyHtml, docTitle = 'Report') => {
  const win = window.open('', '_blank', 'width=1024,height=768');
  if (!win) return false;
  win.document.open();
  win.document.write(
    `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>${escapeHtml(docTitle)}</title>` +
      `<style>` +
      `*{box-sizing:border-box}` +
      `body{font-family:'Segoe UI',Arial,sans-serif;color:#101828;margin:28px;font-size:12px}` +
      `h1{font-size:20px;margin:0 0 4px}` +
      `.sub{color:#667085;font-size:12px;margin:0 0 18px}` +
      `.filters{display:flex;flex-wrap:wrap;gap:8px;margin:0 0 16px}` +
      `.filters .f{border:1px solid #eaecf0;border-radius:8px;padding:5px 10px;background:#f8f9fb}` +
      `.filters .fk{display:block;font-size:9px;text-transform:uppercase;letter-spacing:.04em;color:#98a2b3;font-weight:700}` +
      `.filters .fv{display:block;font-size:12px;font-weight:700;color:#101828}` +
      `.kpis{display:flex;gap:12px;flex-wrap:wrap;margin:0 0 18px}` +
      `.kpi{border:1px solid #eaecf0;border-radius:10px;padding:10px 14px;min-width:150px}` +
      `.kpi .l{color:#667085;font-size:11px;text-transform:uppercase;letter-spacing:.04em}` +
      `.kpi .v{font-size:18px;font-weight:700;margin-top:4px}` +
      `table{border-collapse:collapse;width:100%}` +
      `th,td{border:1px solid #eaecf0;padding:7px 9px;text-align:left;font-size:11px}` +
      `th{background:#f9fafb;font-weight:600;color:#475467}` +
      `tbody tr:nth-child(even){background:#fcfcfd}` +
      `.r{text-align:right}` +
      `@media print{@page{size:A4 landscape;margin:12mm}body{margin:0}}` +
      `</style></head><body>${bodyHtml}</body></html>`
  );
  win.document.close();
  win.focus();
  // Give the new document a tick to lay out before invoking print.
  setTimeout(() => {
    win.print();
  }, 350);
  return true;
};

// Renders an "Applied Filters" chip row for the print/PDF view. filterLines is
// an array of [label, value] pairs (only meaningful ones — build with
// buildFilterLines). Returns '' when there are none.
export const filtersToPrintHtml = (filterLines = []) =>
  (filterLines && filterLines.length)
    ? `<div class="filters">${filterLines
        .map(([k, v]) => `<span class="f"><span class="fk">${escapeHtml(k)}</span><span class="fv">${escapeHtml(v)}</span></span>`)
        .join('')}</div>`
    : '';

// Turns an ordered [label, value] spec into filter lines, keeping only entries
// whose value is a non-empty string (so blank criteria are omitted).
export const buildFilterLines = (pairs = []) =>
  pairs.filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '');

export const STATUS_BADGE_KEYS = {
  Confirmed: 'confirmed',
  Completed: 'confirmed',
  Pending: 'pending',
  Draft: 'pending',
  Cancelled: 'cancelled',
};
